import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import JSZip from 'jszip';
import { GRADING_THRESHOLDS } from '../../../shared/constants.js';
import { Exam, type ExamState } from '../../exam.js';
import { Submission } from '../../submission.js';
import {
  buildSubmissionReportPDFBytes,
  buildTeacherSummaryCsv,
  buildTeacherSummaryPDFBytes,
  createTeacherSummary,
} from '../generate-grading-reports.js';

export const downloadSummaryReportsToolDef = [
  {
    type: 'function',
    function: {
      name: 'download_summary_reports',
      description:
        'Löst den Download des Auswertungsberichts aus. Für "pdf": ZIP-Datei mit allen Schülerkorrekturen, Lehrerzusammenfassung und CSV. Für "csv": nur die Notenübersicht als CSV. Darf NUR aufgerufen werden, wenn der Nutzer explizit danach fragt.',
      parameters: {
        type: 'object',
        properties: {
          examId: {
            type: 'string',
            description: 'Die ID der aktuellen Klausur',
          },
          type: {
            type: 'string',
            enum: ['pdf', 'csv'],
            description:
              '"pdf" für den vollständigen Auswertungsbericht als ZIP (enthält alle Schüler-PDFs, Lehrerbericht und CSV). "csv" für nur die Notenübersicht als CSV-Datei.',
          },
        },
        required: ['examId', 'type'],
      },
    },
  },
];

export async function handleDownloadSummaryReports(exam: Exam, toolCallArgs: any): Promise<object> {
  const examIdToUse = toolCallArgs.examId || exam?._id?.toString();
  const downloadType: 'pdf' | 'csv' = toolCallArgs.type ?? 'pdf';

  if (!examIdToUse) {
    return { content: 'Fehler: Keine Klausur-ID.' };
  }

  const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
  const currentUserId = currentUser?._id;
  if (!currentUserId) {
    return { content: 'Fehler: Nutzer nicht eingeloggt.' };
  }

  try {
    const examCollection = await getCollection(Exam);
    const submissionCollection = await getCollection(Submission);

    const currentExam = await examCollection.findOne({ _id: examIdToUse });
    if (!currentExam) {
      return { content: 'Fehler: Klausur nicht gefunden.' };
    }

    const submissions = await submissionCollection
      .find({ examId: examIdToUse })
      .sort({ submittedAt: 1, _id: 1 })
      .toArray();

    if (submissions.length === 0) {
      return {
        content:
          'SYSTEM INFO: Es gibt noch keine Schülerabgaben für diese Klausur. Bitte den Nutzer darauf hinweisen.',
      };
    }

    const zipDate = new Date().toISOString().slice(0, 10);

    if (downloadType === 'csv') {
      const csvBuffer = buildTeacherSummaryCsv({ exam: currentExam, submissions: submissions as any[] });
      const csvFilename = `notenuebersicht_${zipDate}.csv`;
      const file = await AdornisFile.createFromBuffer(csvBuffer, csvFilename, {
        userID: String(currentUserId),
        examId: String(examIdToUse),
        fileType: 'gradebook-csv',
      });
      if (!file._id) {
        return { content: 'Fehler: CSV-Datei konnte nicht erstellt werden.' };
      }
      return {
        content: 'SYSTEM INFO: Der Download der Notenübersicht (CSV) wird jetzt ausgelöst.',
        pendingDownload: file._id,
      };
    }

    // PDF: check for unresolved uncertainties before generating
    const teacherSummary = createTeacherSummary(currentExam, submissions as any[]);
    const isAnySubmissionNotReady = teacherSummary.studentSummaries.some(
      student =>
        teacherSummary.activeTaskDefinitions.some(taskDef => student.isApprovedByTask[taskDef.taskKey] !== true) ||
        student.hasOpenLanguageUseClarification,
    );

    if (isAnySubmissionNotReady) {
      return {
        content: `SYSTEM INFO: Die PDF-Berichte können noch nicht erstellt werden, weil es noch offene KI-Unsicherheiten gibt (Konfidenz unter ${Math.round(GRADING_THRESHOLDS.MEDIUM_CONFIDENCE * 100)}% ohne manuelle Freigabe). Bitte den Nutzer darauf hinweisen, dass er zuerst alle Unsicherheiten im Korrektur-Canvas manuell prüfen und freigeben muss.`,
      };
    }

    const zip = new JSZip();
    const reportFolder = zip.folder('berichte');

    for (let index = 0; index < submissions.length; index += 1) {
      const submission = submissions[index];
      if (!submission) continue;
      const pdfBytes = await buildSubmissionReportPDFBytes({
        exam: currentExam,
        submission,
        submissionIndex: index,
      });
      const studentId = String((submission as any).studentId ?? '').trim().replace(/\s+/g, '_') || `abgabe-${index + 1}`;
      reportFolder?.file(`bericht-${studentId}.pdf`, Buffer.from(pdfBytes));
    }

    const teacherReportBytes = await buildTeacherSummaryPDFBytes({ exam: currentExam, submissions });
    zip.file(`lehrerbericht_${zipDate}.pdf`, Buffer.from(teacherReportBytes));
    zip.file(`notenuebersicht_${zipDate}.csv`, buildTeacherSummaryCsv({ exam: currentExam, submissions: submissions as any[] }));

    const zipBuffer = await zip.generateAsync({ type: 'nodebuffer', compression: 'DEFLATE', compressionOptions: { level: 6 } });
    const zipFilename = `auswertungsbericht_${zipDate}.zip`;
    const zipFile = await AdornisFile.createFromBuffer(zipBuffer, zipFilename, {
      userID: String(currentUserId),
      examId: String(examIdToUse),
      fileType: 'grading-report-zip',
    });

    if (!zipFile._id) {
      return { content: 'Fehler: ZIP-Datei konnte nicht erstellt werden.' };
    }

    return {
      content: 'SYSTEM INFO: Der Download des Auswertungsberichts (ZIP mit allen PDFs und Notenübersicht) wird jetzt ausgelöst.',
      pendingDownload: zipFile._id,
    };
  } catch (err) {
    logger.error({ err, examIdToUse }, 'download_summary_reports: Fehler beim Erstellen der Datei');
    return { content: `Fehler beim Erstellen der Download-Datei: ${(err as Error).message}` };
  }
}

export function downloadSummaryReportsAvailability(_examState: ExamState): boolean {
  return true;
}
